import { useContext } from "react";

import { paths } from "routes/routes";
import { AppContext } from "context/AppContext";
import { useFetch } from "hooks/useFetch";
import { initializeApp } from "firebase/app";
import { getAnalytics } from "firebase/analytics";
import { Container } from "components/Container/Container";
import { CreditCalculator } from "components/CreditCalculator/CreditCalculator";
import { DownloadsSection } from "components/DownloadsSection/DownloadsSection";
import { Footer } from "components/Footer/Footer";
import { Fq } from "components/Fq/Fq";
import { HeroBanner } from "components/HeroBanner/HeroBanner";
import { HowWorks } from "components/HowWorks/HowWorks";
import { Navbar } from "components/Navbar/Navbar";
import { TrustBanner } from "components/TrustBanner/TrustBanner";
import { WhereCanIPay } from "components/WhereCanIPay/WhereCanIPay";
import { SEO } from "components/SEO";
import 'animate.css';


const firebaseConfig = {
  apiKey: "AIzaSyCEE4bf1lLeVCSZjg5Eah6mCAaZrDKTOOk",
  authDomain: "efektivos-dev-a03c3.firebaseapp.com",
  projectId: "efektivos-dev-a03c3",
  storageBucket: "efektivos-dev-a03c3.appspot.com",
  messagingSenderId: "384883924229",
  appId: "1:384883924229:web:4d6a200da4df5f9ed82b6c",
  measurementId: "G-TQW3NYPRSB"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const analytics = getAnalytics(app);

// Structured Data - Organization / FinancialService
const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "FinancialService",
  "@id": "https://efektivos.com/#organization",
  "name": "Efektivos Guatemala",
  "alternateName": "Efektivos",
  "url": "https://efektivos.com",
  "logo": {
    "@type": "ImageObject",
    "url": "https://efektivos.com/assets/efektivos_logo.png",
    "width": 200,
    "height": 200
  },
  "image": "https://efektivos.com/assets/efektivos_og.png",
  "description": "Préstamos rápidos y créditos personales en Guatemala. Solicita tu préstamo desde la app con requisitos mínimos, aprobación inmediata y desembolso el mismo día.",
  "slogan": "Tu préstamo de la manera más fácil",
  "address": {
    "@type": "PostalAddress",
    "addressCountry": "GT",
    "addressLocality": "Ciudad de Guatemala",
    "addressRegion": "Guatemala"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": "14.634915",
    "longitude": "-90.506882"
  },
  "areaServed": {
    "@type": "Country",
    "name": "Guatemala"
  },
  "serviceType": [
    "Préstamos personales",
    "Créditos rápidos",
    "Préstamos en línea",
    "Préstamos inmediatos",
    "Microcréditos"
  ],
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Servicios de Préstamos",
    "itemListElement": [
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Préstamos Personales",
          "description": "Préstamos personales rápidos con requisitos mínimos en Guatemala"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Créditos Rápidos",
          "description": "Créditos con aprobación inmediata y desembolso el mismo día"
        }
      }
    ]
  },
  "sameAs": [
    "https://play.google.com/store/apps/details?id=com.efektivos",
    "https://www.facebook.com/gt.efektivos",
    "https://www.instagram.com/efektivos/"
  ],
  "potentialAction": {
    "@type": "SearchAction",
    "target": "https://efektivos.com/?q={search_term_string}",
    "query-input": "required name=search_term_string"
  }
};

// Structured Data - MobileApplication
const mobileAppSchema = {
  "@context": "https://schema.org",
  "@type": "MobileApplication",
  "name": "Efektivos",
  "operatingSystem": "Android",
  "applicationCategory": "FinanceApplication",
  "description": "App de préstamos rápidos en Guatemala. Solicita créditos personales con requisitos mínimos desde tu celular.",
  "offers": {
    "@type": "Offer",
    "price": "0",
    "priceCurrency": "GTQ"
  },
  "installUrl": "https://play.google.com/store/apps/details?id=com.efektivos",
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "4.5",
    "ratingCount": "1000",
    "bestRating": "5",
    "worstRating": "1"
  },
  "author": {
    "@type": "Organization",
    "name": "Efektivos Guatemala"
  }
};

// Genera FAQPage schema desde los FAQs dinamicos
const generateFAQSchema = (faqs) => {
  if (!faqs || faqs.length === 0) return null;

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
};

export const Home = () => {
	const { countryData } = useContext(AppContext);
	
	const state = useFetch(
		"https://us-central1-efektivos-dev-a03c3.cloudfunctions.net/api/v1/landing-page",
		countryData
	);

	const { faqs = [], credit_types = [], countries = [] } = state.data || {};
	const { loading, error } = state;

	// Combinar schemas para structured data (Organization + App + FAQs)
	const faqSchema = generateFAQSchema(faqs);
	const structuredData = faqSchema
		? [organizationSchema, mobileAppSchema, faqSchema]
		: [organizationSchema, mobileAppSchema];

	return (
		<>
			<SEO
				title="Préstamos Rápidos y Créditos Personales"
				description="Préstamos rápidos con Efektivos: requisitos mínimos, autorización en tiempo récord y desembolso inmediato en Guatemala. Descarga la app y solicita tu crédito hoy."
				path="/"
				structuredData={structuredData}
			/>
			<Navbar countries={countries} />
			<div id={paths.home.slice(1)} className="h-4 mb-8 xl:mb-14 w-full"></div>
			<HeroBanner />
			<TrustBanner />
			<div className="relative z-10 bg-white">
				<Container className="pt-2">
					<div id={paths.calculator.slice(1)} className="h-4 my-16 w-full"></div>
					<CreditCalculator credit_types={credit_types} isLoading={loading} error={error}/>
					<div id={paths.fq.slice(1)} className="h-0 my-16 w-full"></div>
					<Fq faqs={faqs} isLoading={loading} error={error} />
					<div id={paths.downloads.slice(1)} className="w-full my-16"></div>
					<DownloadsSection />
					<div id={paths.how_works.slice(1)} className="w-full"></div>
					<HowWorks />
					<div className=" w-full"></div>
					<WhereCanIPay />
					<div id={paths.contact.slice(1)} className="w-full"
					></div>
					{/* <Contact /> */}
					<div className="w-full"></div>
				</Container>
				<Footer />
			</div>
		</>
	);
};
